import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { getInvoiceById, markInvoiceRefunded } from '@/api/invoices'
import { useAuthStore } from '@/store/authStore'
import { hasPermission } from '@/lib/permissions'
import { formatCustomerNumber } from '@/lib/customer'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { InvoiceStatusBadge } from '@/components/InvoiceStatusBadge'
import { InvoiceVoidAction } from '@/components/invoice/InvoiceVoidAction'
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format'
import { canVoidInvoice, formatInvoiceEvent, formatInvoiceLabel, getOutstandingRefund } from '@/lib/invoice'
import type { InvoiceDetail, InvoiceEvent, InvoiceLine } from '@/api/types'

const NOT_FOUND_STATUS = 404
const INVALID_ID_MESSAGE = 'Ogiltigt faktura-id.'
const NOT_FOUND_MESSAGE = 'Fakturan hittades inte.'
const ERROR_MESSAGE = 'Kunde inte hämta fakturan just nu.'
const LOADING_MESSAGE = 'Laddar faktura...'
const NO_LINES_MESSAGE = 'Raderna saknas – fakturan registrerades innan de började sparas.'
const NO_EVENTS_MESSAGE = 'Ingen historik ännu.'
const CONTRACT_LABEL = 'Avtal'
const EXTERNAL_LABEL = 'Utan leverans'
const REFUND_DESCRIPTION = 'Fakturan makulerades efter att kunden betalat. Beloppet ska betalas tillbaka till kunden för hand.'
const MARK_REFUNDED_LABEL = 'Markera som återbetald'
const MARK_REFUNDED_CONFIRM_MESSAGE = 'Har beloppet betalats tillbaka till kunden? Markeringen går inte att ångra.'
const MARK_REFUNDED_ERROR_MESSAGE = 'Kunde inte markera återbetalningen just nu. Försök igen.'
const EMPTY_VALUE = '–'

function formatOptionalDate(value: string | null): string {
  if (value === null) {
    return EMPTY_VALUE
  }

  return formatDate(value)
}

function InvoiceLineRow({ line }: { line: InvoiceLine }): React.ReactElement {
  return (
    <tr className="border-t border-border/70">
      <td className="px-6 py-3">{line.description}</td>
      <td className="px-6 py-3 text-right tabular-nums">{line.item_count}</td>
      <td className="px-6 py-3 text-right tabular-nums">{formatCurrency(Number(line.unit_amount))}</td>
      <td className="px-6 py-3 text-right tabular-nums">{formatCurrency(Number(line.row_amount))}</td>
      <td className="px-6 py-3 text-muted-foreground">{line.vat_code}</td>
    </tr>
  )
}

function InvoiceEventItem({ event }: { event: InvoiceEvent }): React.ReactElement {
  return (
    <li className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-3 py-3">
      <span className="shrink-0 whitespace-nowrap text-sm text-muted-foreground tabular-nums">{formatDateTime(event.created_at)}</span>
      <span>{formatInvoiceEvent(event)}</span>
    </li>
  )
}

function InvoiceReferenceValue({ invoice }: { invoice: InvoiceDetail }): React.ReactElement {
  if (invoice.shipment_id === null) {
    return <p>{invoice.source === 'external' ? EXTERNAL_LABEL : CONTRACT_LABEL}</p>
  }

  return (
    <Link
      to={`/shipments/${invoice.shipment_id}`}
      className="underline"
    >
      Leverans
    </Link>
  )
}

export function InvoiceDetailPage(): React.ReactElement {
  const { invoiceId } = useParams()
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isMarkingRefunded, setIsMarkingRefunded] = useState(false)
  const [refundErrorMessage, setRefundErrorMessage] = useState<string | null>(null)
  const user = useAuthStore((state) => state.user)

  const hasError = errorMessage !== null
  const showInvoice = !isLoading && !hasError && invoice !== null
  const hasLines = invoice !== null && invoice.lines.length > 0
  const hasEvents = invoice !== null && invoice.events.length > 0
  const outstandingRefund = invoice === null ? 0 : getOutstandingRefund(invoice)
  const hasOutstandingRefund = outstandingRefund > 0
  const isRefunded = invoice !== null && invoice.refund_due !== null && invoice.refunded_at !== null
  const canMarkRefunded = hasPermission(user, 'invoicing.refund')
  const canVoid = invoice !== null && canVoidInvoice(invoice) && hasPermission(user, 'invoicing.void')
  const hasRefundError = refundErrorMessage !== null

  const handleMarkRefunded = async (): Promise<void> => {
    if (invoice === null || !window.confirm(MARK_REFUNDED_CONFIRM_MESSAGE)) {
      return
    }

    setIsMarkingRefunded(true)
    setRefundErrorMessage(null)

    try {
      setInvoice(await markInvoiceRefunded(invoice.id))
    } catch {
      setRefundErrorMessage(MARK_REFUNDED_ERROR_MESSAGE)
    } finally {
      setIsMarkingRefunded(false)
    }
  }

  useEffect(() => {
    const id = Number(invoiceId)

    if (!Number.isInteger(id) || id < 1) {
      setErrorMessage(INVALID_ID_MESSAGE)
      return
    }

    const loadInvoice = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        setInvoice(await getInvoiceById(id))
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === NOT_FOUND_STATUS) {
          setErrorMessage(NOT_FOUND_MESSAGE)
          return
        }

        setErrorMessage(ERROR_MESSAGE)
      } finally {
        setIsLoading(false)
      }
    }

    void loadInvoice()
  }, [invoiceId])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-serif text-primary">Faktura</h1>
        <Button
          asChild
          variant="outline"
          className="rounded-none"
        >
          <Link to="/invoices">Tillbaka</Link>
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">{LOADING_MESSAGE}</p>}

      {hasError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {showInvoice && invoice && (
        <>
          <Card className="shadow-none border-border/80">
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>{formatInvoiceLabel(invoice)}</CardTitle>
                <CardDescription>{invoice.customer_name ?? 'Okänd kund'}</CardDescription>
              </div>
              <div className="flex flex-col items-end gap-3">
                <InvoiceStatusBadge status={invoice.status} />
                {canVoid && (
                  <InvoiceVoidAction
                    invoice={invoice}
                    onVoided={setInvoice}
                  />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Kund</p>
                  <Link
                    to={`/customers/${invoice.customer_id}`}
                    className="underline"
                  >
                    {invoice.customer_name ?? 'Okänd kund'}
                  </Link>
                  <p className="text-sm text-muted-foreground">{formatCustomerNumber(invoice.customer_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avser</p>
                  <InvoiceReferenceValue invoice={invoice} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fakturadatum</p>
                  <p>{formatOptionalDate(invoice.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Förfaller</p>
                  <p>{formatOptionalDate(invoice.due_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Belopp</p>
                  <p className="tabular-nums">{formatCurrency(Number(invoice.amount))}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Betalt</p>
                  <p className="tabular-nums">{formatCurrency(Number(invoice.paid_sum))}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Återstår</p>
                  <p className="tabular-nums">{formatCurrency(Number(invoice.remaining_sum))}</p>
                </div>
                {isRefunded && (
                  <div>
                    <p className="text-sm text-muted-foreground">Återbetalt</p>
                    <p className="tabular-nums">
                      {formatCurrency(Number(invoice.refund_due))} · {formatOptionalDate(invoice.refunded_at)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {hasOutstandingRefund && (
            <Card className="shadow-none border-amber-200 bg-amber-50">
              <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1.5">
                  <CardTitle>Återbetalning krävs: {formatCurrency(outstandingRefund)}</CardTitle>
                  <CardDescription className="text-amber-900">{REFUND_DESCRIPTION}</CardDescription>
                </div>
                {canMarkRefunded && (
                  <Button
                    className="shrink-0 rounded-none"
                    disabled={isMarkingRefunded}
                    onClick={handleMarkRefunded}
                  >
                    {MARK_REFUNDED_LABEL}
                  </Button>
                )}
              </CardHeader>
              {hasRefundError && <CardContent className="text-sm text-destructive">{refundErrorMessage}</CardContent>}
            </Card>
          )}

          <Card className="shadow-none border-border/80">
            <CardHeader>
              <CardTitle>Rader</CardTitle>
              <CardDescription>Det som fakturerades.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {hasLines && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-muted/50 text-left text-muted-foreground">
                      <tr>
                        <th className="px-6 py-3 font-medium">Beskrivning</th>
                        <th className="px-6 py-3 font-medium text-right">Antal</th>
                        <th className="px-6 py-3 font-medium text-right">À-pris</th>
                        <th className="px-6 py-3 font-medium text-right">Summa</th>
                        <th className="px-6 py-3 font-medium">Moms</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.lines.map((line, index) => (
                        <InvoiceLineRow
                          key={index}
                          line={line}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!hasLines && <p className="px-6 py-6 text-sm text-muted-foreground">{NO_LINES_MESSAGE}</p>}
            </CardContent>
          </Card>

          <Card className="shadow-none border-border/80">
            <CardHeader>
              <CardTitle>Historik</CardTitle>
              <CardDescription>Vad som hänt med fakturan, senaste först. Inbetalningar dateras när vår synk såg dem.</CardDescription>
            </CardHeader>
            <CardContent>
              {hasEvents && (
                <ul className="divide-y divide-border/60 border border-border/70">
                  {invoice.events.map((event) => (
                    <InvoiceEventItem
                      key={event.id}
                      event={event}
                    />
                  ))}
                </ul>
              )}
              {!hasEvents && <p className="text-sm text-muted-foreground">{NO_EVENTS_MESSAGE}</p>}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
